// Element factories and the retroactive-restyle rule. Pure — SSR/Node safe.
//
// Text heights: a TextElement's `height` is derived from its wrapped text. The
// factories here set an ESTIMATE (one line); the editor replaces it with the
// measured value (render.ts `measureTextHeightMm`) whenever text or style
// changes. Nothing here touches a canvas.

import { DEFAULT_FONT, getFont, isFontKey, type FontKey } from "./fonts";
import { getOrnament, type OrnamentKey } from "./ornaments";
import { PALETTE_HEX } from "./palette";
import {
  LABEL_MM,
  LIMITS,
  newId,
  type BottleType,
  type CanvasData,
  type EllipseElement,
  type ImageElement,
  type LabelElement,
  type LineElement,
  type OrnamentElement,
  type RectElement,
  type TextAlign,
  type TextElement,
  type TextWeight,
} from "./schema";

/** Styles the inspector edits. With nothing selected they are the defaults for the next element. */
export interface StyleDefaults {
  font: FontKey;
  size_mm: number;
  /** Text colour. */
  color: string;
  align: TextAlign;
  weight: TextWeight;
  italic: boolean;
  letter_spacing_em: number;
  line_height: number;
  /** Frame / divider colour. */
  stroke: string;
  /** Frame border width / divider thickness, mm. */
  stroke_mm: number;
  /** Rectangle / oval fill, null = none. */
  fill: string | null;
}

export type StylePatch = Partial<StyleDefaults>;

export const DEFAULT_STYLE: StyleDefaults = {
  font: DEFAULT_FONT,
  size_mm: 5,
  color: PALETTE_HEX.charcoal,
  align: "center",
  weight: "regular",
  italic: false,
  letter_spacing_em: 0.02,
  line_height: 1.2,
  stroke: PALETTE_HEX.gold,
  stroke_mm: 0.5,
  fill: null,
};

/** Default margin between a new element and the label edge, mm. */
export const LABEL_MARGIN_MM = 6;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round2 = (v: number) => Math.round(v * 100) / 100;

/** One-line text box height estimate (mm) from the fallback font metrics. */
export function estimateLineHeightMm(el: Pick<TextElement, "font" | "size_mm" | "line_height">): number {
  const m = getFont(el.font).metrics;
  return round2((el.size_mm / m.capHeight) * el.line_height);
}

export type FrameKind = "rect" | "ellipse" | "line";

/**
 * A new text element. `at` is the tap point in label mm (the box is centred on
 * it and clamped inside the label); null centres it on the label.
 */
export function createText(
  doc: Pick<CanvasData, "label">,
  at: { x: number; y: number } | null,
  style: StyleDefaults = DEFAULT_STYLE,
  text = "",
): TextElement {
  const W = doc.label.width_mm;
  const H = doc.label.height_mm;
  const width = Math.min(W - 2 * LABEL_MARGIN_MM, 64);
  const base: TextElement = {
    id: newId(),
    type: "text",
    x: 0,
    y: 0,
    width,
    height: 0,
    text,
    font: style.font,
    size_mm: style.size_mm,
    color: style.color,
    align: style.align,
    weight: style.weight,
    italic: style.italic,
    letter_spacing_em: style.letter_spacing_em,
    line_height: style.line_height,
  };
  base.height = estimateLineHeightMm(base);
  const cx = at ? at.x : W / 2;
  const cy = at ? at.y : H / 2;
  base.x = round2(clamp(cx - width / 2, 0, W - width));
  base.y = round2(clamp(cy - base.height / 2, 0, Math.max(0, H - base.height)));
  return base;
}

/** A new frame: rectangle border, oval cartouche or horizontal divider. */
export function createFrame(
  doc: Pick<CanvasData, "label">,
  kind: FrameKind,
  style: StyleDefaults = DEFAULT_STYLE,
): RectElement | EllipseElement | LineElement {
  const W = doc.label.width_mm;
  const H = doc.label.height_mm;
  const stroke_mm = clamp(style.stroke_mm, LIMITS.strokeMm.min, LIMITS.strokeMm.max);
  if (kind === "line") {
    const width = round2(W * 0.5);
    return {
      id: newId(),
      type: "line",
      x: round2((W - width) / 2),
      y: round2(H * 0.62),
      width,
      height: stroke_mm,
      stroke: style.stroke,
      thickness_mm: stroke_mm,
    };
  }
  if (kind === "ellipse") {
    const width = round2(W * 0.66);
    const height = round2(H * 0.42);
    return {
      id: newId(),
      type: "ellipse",
      x: round2((W - width) / 2),
      y: round2((H - height) / 2),
      width,
      height,
      stroke: style.stroke,
      stroke_mm,
      fill: style.fill,
    };
  }
  const m = LABEL_MARGIN_MM / 2 + 1; // a border sits closer to the edge than content
  return {
    id: newId(),
    type: "rect",
    x: m,
    y: m,
    width: round2(W - 2 * m),
    height: round2(H - 2 * m),
    stroke: style.stroke,
    stroke_mm,
    fill: style.fill,
  };
}

/** A new ornament's width as a fraction of the label width. */
export const ORNAMENT_WIDTH_FRACTION = 0.3;
/** How far a repeat of the same ornament is offset (x and y), mm. */
export const ORNAMENT_REPEAT_OFFSET_MM = 4;

/**
 * A new ornament: 30 % of the label width (height from the ornament's aspect,
 * shrunk if a tall ornament would not fit), centred on the label and clamped
 * inside it. Each existing ornament of the same key moves the new one a
 * further 4 mm right and down, so repeats are visibly separate.
 */
export function createOrnament(
  doc: Pick<CanvasData, "label" | "elements">,
  key: OrnamentKey,
  color: string,
): OrnamentElement {
  const W = doc.label.width_mm;
  const H = doc.label.height_mm;
  const aspect = getOrnament(key)?.aspect ?? 1;
  let width = W * ORNAMENT_WIDTH_FRACTION;
  let height = width / aspect;
  const maxH = H - 2 * LABEL_MARGIN_MM;
  if (height > maxH) {
    height = maxH;
    width = height * aspect;
  }
  width = round2(width);
  height = round2(height);
  const repeats = doc.elements.filter((e) => e.type === "ornament" && e.asset === key).length;
  const offset = repeats * ORNAMENT_REPEAT_OFFSET_MM;
  return {
    id: newId(),
    type: "ornament",
    asset: key,
    color,
    x: round2(clamp((W - width) / 2 + offset, 0, W - width)),
    y: round2(clamp((H - height) / 2 + offset, 0, H - height)),
    width,
    height,
  };
}

/** Fit a w×h aspect inside a box (contain), centred. */
export function fitContain(
  natural: { width: number; height: number },
  box: { x: number; y: number; width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  const nw = Math.max(1, natural.width);
  const nh = Math.max(1, natural.height);
  const s = Math.min(box.width / nw, box.height / nh);
  const width = round2(nw * s);
  const height = round2(nh * s);
  return {
    x: round2(box.x + (box.width - width) / 2),
    y: round2(box.y + (box.height - height) / 2),
    width,
    height,
  };
}

/**
 * Place the mock-up's single uploaded image (locked decision 4: one image).
 * The image always sits at the BACK of the stack (index 0 — array order is
 * drawing order), so text and frames always draw over it.
 * - No image yet: fitted inside the label with a 6 mm margin.
 * - An image already present: the new picture replaces it, contained in the
 *   old image's box, keeping its id — and is moved to the back if an older
 *   document had it anywhere else.
 * `natural` is the decoded image's pixel size (only its aspect matters).
 */
export function placeImage(
  doc: CanvasData,
  natural: { width: number; height: number },
  margin = LABEL_MARGIN_MM,
): { doc: CanvasData; element: ImageElement } {
  const existing = doc.elements.find((e) => e.type === "image") as ImageElement | undefined;
  if (existing) {
    const element: ImageElement = { ...existing, ...fitContain(natural, existing) };
    return {
      element,
      doc: { ...doc, elements: [element, ...doc.elements.filter((e) => e.id !== existing.id)] },
    };
  }
  const W = doc.label.width_mm;
  const H = doc.label.height_mm;
  const box = fitContain(natural, {
    x: margin,
    y: margin,
    width: W - 2 * margin,
    height: H - 2 * margin,
  });
  const element: ImageElement = { id: newId(), type: "image", src: "uploaded", fit: "contain", ...box };
  return { element, doc: { ...doc, elements: [element, ...doc.elements] } };
}

/**
 * "I have my own artwork": the finished design becomes the single image,
 * fitted to the WHOLE label (0 mm margin, contain — an artwork whose aspect
 * differs from the label shows the background as bars), and every other
 * element is cleared. The existing image keeps its id. `natural` is the
 * decoded image's pixel size (only its aspect matters).
 */
export function placeArtwork(
  doc: CanvasData,
  natural: { width: number; height: number },
): { doc: CanvasData; element: ImageElement } {
  const existing = doc.elements.find((e) => e.type === "image") as ImageElement | undefined;
  const box = fitContain(natural, { x: 0, y: 0, width: doc.label.width_mm, height: doc.label.height_mm });
  const element: ImageElement = { id: existing?.id ?? newId(), type: "image", src: "uploaded", fit: "contain", ...box };
  return { element, doc: { ...doc, elements: [element] } };
}

/** The style an element currently shows, for filling the inspector. */
export function styleOf(el: LabelElement | null, defaults: StyleDefaults): StyleDefaults {
  if (!el) return defaults;
  switch (el.type) {
    case "text":
      return {
        ...defaults,
        font: el.font,
        size_mm: el.size_mm,
        color: el.color,
        align: el.align,
        weight: el.weight,
        italic: el.italic,
        letter_spacing_em: el.letter_spacing_em,
        line_height: el.line_height,
      };
    case "rect":
    case "ellipse":
      return { ...defaults, stroke: el.stroke, stroke_mm: el.stroke_mm, fill: el.fill ?? null };
    case "line":
      return { ...defaults, stroke: el.stroke, stroke_mm: el.thickness_mm };
    case "ornament":
      // One colour; shown in both the text and the frame colour rows.
      return { ...defaults, color: el.color, stroke: el.color };
    default:
      return defaults;
  }
}

/** Clamp a style patch to the editor limits. */
export function sanitiseStyle(patch: StylePatch): StylePatch {
  const out: StylePatch = { ...patch };
  if (out.font !== undefined && !isFontKey(out.font)) delete out.font;
  if (out.size_mm !== undefined)
    out.size_mm = round2(clamp(out.size_mm, LIMITS.textSizeMm.min, LIMITS.textSizeMm.max));
  if (out.stroke_mm !== undefined)
    out.stroke_mm = round2(clamp(out.stroke_mm, LIMITS.strokeMm.min, LIMITS.strokeMm.max));
  if (out.letter_spacing_em !== undefined)
    out.letter_spacing_em = round2(
      clamp(out.letter_spacing_em, LIMITS.letterSpacingEm.min, LIMITS.letterSpacingEm.max),
    );
  if (out.line_height !== undefined)
    out.line_height = round2(clamp(out.line_height, LIMITS.lineHeight.min, LIMITS.lineHeight.max));
  return out;
}

/**
 * Apply a style patch to one element (the retroactive half of the restyle
 * rule). Keys that do not apply to the element's type are ignored. For frames
 * and dividers `stroke` wins, but a bare `color` also recolours them, so one
 * colour swatch row can serve every element type. Ornaments take a colour
 * only (`color`, else `stroke`); every other key ignores them. Text `height`
 * is NOT re-measured here — the caller does that (it needs a canvas).
 */
export function applyStyle(el: LabelElement, raw: StylePatch): LabelElement {
  const p = sanitiseStyle(raw);
  switch (el.type) {
    case "text": {
      const next: TextElement = { ...el };
      if (p.font !== undefined) next.font = p.font;
      if (p.size_mm !== undefined) next.size_mm = p.size_mm;
      if (p.color !== undefined) next.color = p.color;
      if (p.align !== undefined) next.align = p.align;
      if (p.weight !== undefined) next.weight = p.weight;
      if (p.italic !== undefined) next.italic = p.italic;
      if (p.letter_spacing_em !== undefined) next.letter_spacing_em = p.letter_spacing_em;
      if (p.line_height !== undefined) next.line_height = p.line_height;
      return next;
    }
    case "rect":
    case "ellipse": {
      const next = { ...el };
      const colour = p.stroke ?? p.color;
      if (colour !== undefined) next.stroke = colour;
      if (p.stroke_mm !== undefined) next.stroke_mm = p.stroke_mm;
      if (p.fill !== undefined) next.fill = p.fill;
      return next;
    }
    case "line": {
      const next: LineElement = { ...el };
      const colour = p.stroke ?? p.color;
      if (colour !== undefined) next.stroke = colour;
      if (p.stroke_mm !== undefined) {
        // Keep the divider centred on the same line as it thickens.
        const cy = el.y + el.height / 2;
        next.thickness_mm = p.stroke_mm;
        next.height = p.stroke_mm;
        next.y = round2(cy - p.stroke_mm / 2);
      }
      return next;
    }
    case "ornament": {
      const colour = p.color ?? p.stroke;
      return colour !== undefined && colour !== el.color ? { ...el, color: colour } : el;
    }
    default:
      return el;
  }
}

/**
 * Switch the bottle. The label size changes (wine 90×100 ↔ MCC 80×100), so the
 * whole design is scaled uniformly to fit the new label and centred — never
 * stretched, so type, images and ornaments keep their proportions.
 */
export function rebottle(doc: CanvasData, bottle: BottleType): CanvasData {
  if (doc.bottle === bottle) return doc;
  const mm = LABEL_MM[bottle];
  const oldW = doc.label.width_mm;
  const oldH = doc.label.height_mm;
  const s = Math.min(mm.width / oldW, mm.height / oldH);
  const ox = (mm.width - oldW * s) / 2;
  const oy = (mm.height - oldH * s) / 2;
  const elements = doc.elements.map((el): LabelElement => {
    const box = {
      x: round2(el.x * s + ox),
      y: round2(el.y * s + oy),
      width: round2(el.width * s),
      height: round2(el.height * s),
    };
    switch (el.type) {
      case "text":
        return { ...el, ...box, size_mm: round2(el.size_mm * s) };
      case "rect":
      case "ellipse":
        return {
          ...el,
          ...box,
          stroke_mm: round2(clamp(el.stroke_mm * s, LIMITS.strokeMm.min, LIMITS.strokeMm.max)),
        };
      case "line": {
        const t = round2(clamp(el.thickness_mm * s, LIMITS.strokeMm.min, LIMITS.strokeMm.max));
        return { ...el, ...box, height: t, thickness_mm: t };
      }
      default:
        return { ...el, ...box };
    }
  });
  return {
    ...doc,
    bottle,
    label: { width_mm: mm.width, height_mm: mm.height },
    elements,
  };
}

/** Human name for an element, for aria-live messages and the inspector heading. */
export function describeElement(el: LabelElement): string {
  switch (el.type) {
    case "text": {
      const t = el.text.trim().replace(/\s+/g, " ");
      return t ? `Text “${t.length > 30 ? t.slice(0, 29) + "…" : t}”` : "New text";
    }
    case "image":
      return "Image";
    case "rect":
      return "Rectangle frame";
    case "ellipse":
      return "Oval frame";
    case "line":
      return "Divider";
    case "ornament":
      return getOrnament(el.asset)?.label ?? "Ornament";
  }
}
